import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Business,
  Person,
  CreditCard,
  Check,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';

const steps = ['会社情報', '担当者情報', 'プラン選択', '確認'];

interface FormData {
  // 会社情報
  company_name: string;
  company_url: string;
  company_address: string;
  company_phone: string;
  // 担当者情報
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  // プラン
  plan: 'standard' | 'unlimited';
  // 同意
  agree_terms: boolean;
}

const PartnerApplyPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    company_name: '',
    company_url: '',
    company_address: '',
    company_phone: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    plan: 'standard',
    agree_terms: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const handleChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (step === 0) {
      if (!formData.company_name) newErrors.company_name = '会社名を入力してください';
      if (!formData.company_url) newErrors.company_url = 'WebサイトURLを入力してください';
      if (!formData.company_address) newErrors.company_address = '住所を入力してください';
    }

    if (step === 1) {
      if (!formData.contact_name) newErrors.contact_name = '担当者名を入力してください';
      if (!formData.contact_email) newErrors.contact_email = 'メールアドレスを入力してください';
      if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
        newErrors.contact_email = '正しいメールアドレスを入力してください';
      }
    }

    if (step === 3) {
      if (!formData.agree_terms) newErrors.agree_terms = '利用規約への同意が必要です';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/public/partner-apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: formData.company_name,
          company_url: formData.company_url,
          company_address: formData.company_address,
          company_phone: formData.company_phone || null,
          contact_name: formData.contact_name,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone || null,
          plan: formData.plan,
          agree_terms: formData.agree_terms,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '申請に失敗しました');
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Submit error:', err);
      setError(err.message || '申請に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.50',
          py: 6,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <Check sx={{ fontSize: 50, color: 'success.main' }} />
            </Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              申請を受け付けました
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              審査完了後、ご登録いただいたメールアドレスにご連絡いたします。
              通常2〜3営業日以内にご連絡いたします。
            </Typography>
            <Alert severity="info" sx={{ textAlign: 'left' }}>
              審査完了後、Stripe Connectの設定案内をお送りします。
              設定完了後、すぐに販売を開始できます。
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="md">
        {/* ヘッダー */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            OEMパートナー申請
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gambitの製品を自社ブランドで販売しませんか？
          </Typography>
        </Box>

        {/* ステッパー */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Step 0: 会社情報 */}
          {activeStep === 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Business color="primary" />
                <Typography variant="h6">会社情報</Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="会社名"
                    value={formData.company_name}
                    onChange={handleChange('company_name')}
                    error={!!errors.company_name}
                    helperText={errors.company_name}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="WebサイトURL"
                    value={formData.company_url}
                    onChange={handleChange('company_url')}
                    error={!!errors.company_url}
                    helperText={errors.company_url}
                    placeholder="https://example.com"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="住所"
                    value={formData.company_address}
                    onChange={handleChange('company_address')}
                    error={!!errors.company_address}
                    helperText={errors.company_address}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="電話番号（任意）"
                    value={formData.company_phone}
                    onChange={handleChange('company_phone')}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 1: 担当者情報 */}
          {activeStep === 1 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Person color="primary" />
                <Typography variant="h6">担当者情報</Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="担当者名"
                    value={formData.contact_name}
                    onChange={handleChange('contact_name')}
                    error={!!errors.contact_name}
                    helperText={errors.contact_name}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="メールアドレス"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange('contact_email')}
                    error={!!errors.contact_email}
                    helperText={errors.contact_email}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="電話番号（任意）"
                    value={formData.contact_phone}
                    onChange={handleChange('contact_phone')}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 2: プラン選択 */}
          {activeStep === 2 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CreditCard color="primary" />
                <Typography variant="h6">プラン選択</Typography>
              </Box>
              <FormControl component="fieldset">
                <RadioGroup
                  value={formData.plan}
                  onChange={(e) => setFormData({ ...formData, plan: e.target.value as 'standard' | 'unlimited' })}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      mb: 2,
                      cursor: 'pointer',
                      borderColor: formData.plan === 'standard' ? 'primary.main' : 'divider',
                      borderWidth: formData.plan === 'standard' ? 2 : 1,
                    }}
                    onClick={() => setFormData({ ...formData, plan: 'standard' })}
                  >
                    <FormControlLabel
                      value="standard"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="h6">Standardプラン</Typography>
                          <Typography variant="h4" color="primary" fontWeight="bold">
                            ¥55,000<Typography component="span" variant="body2">/月</Typography>
                          </Typography>
                          <List dense>
                            <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Check color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary="5商品まで販売可能" />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Check color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary="ユーザー1人あたり¥500〜¥700" />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Check color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary="自由な価格設定" />
                            </ListItem>
                          </List>
                        </Box>
                      }
                    />
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      cursor: 'pointer',
                      borderColor: formData.plan === 'unlimited' ? 'primary.main' : 'divider',
                      borderWidth: formData.plan === 'unlimited' ? 2 : 1,
                    }}
                    onClick={() => setFormData({ ...formData, plan: 'unlimited' })}
                  >
                    <FormControlLabel
                      value="unlimited"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="h6">Unlimitedプラン</Typography>
                          <Typography variant="h4" color="primary" fontWeight="bold">
                            ¥110,000<Typography component="span" variant="body2">/月</Typography>
                          </Typography>
                          <List dense>
                            <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Check color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary="全商品販売可能" />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Check color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary="ユーザー1人あたり¥300〜¥500（割引）" />
                            </ListItem>
                            <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Check color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary="優先サポート" />
                            </ListItem>
                          </List>
                        </Box>
                      }
                    />
                  </Paper>
                </RadioGroup>
              </FormControl>
            </Box>
          )}

          {/* Step 3: 確認 */}
          {activeStep === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                申請内容の確認
              </Typography>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">会社情報</Typography>
                  <Typography>{formData.company_name}</Typography>
                  <Typography variant="body2" color="text.secondary">{formData.company_url}</Typography>
                  <Typography variant="body2" color="text.secondary">{formData.company_address}</Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">担当者情報</Typography>
                  <Typography>{formData.contact_name}</Typography>
                  <Typography variant="body2" color="text.secondary">{formData.contact_email}</Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">選択プラン</Typography>
                  <Typography fontWeight="bold">
                    {formData.plan === 'standard' ? 'Standardプラン（¥55,000/月）' : 'Unlimitedプラン（¥110,000/月）'}
                  </Typography>
                </CardContent>
              </Card>

              <Divider sx={{ my: 3 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.agree_terms}
                    onChange={handleChange('agree_terms')}
                  />
                }
                label={
                  <Typography variant="body2">
                    <a href="/terms" target="_blank" rel="noopener noreferrer">利用規約</a>
                    および
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
                    に同意します
                  </Typography>
                }
              />
              {errors.agree_terms && (
                <Typography variant="body2" color="error">
                  {errors.agree_terms}
                </Typography>
              )}
            </Box>
          )}

          {/* ナビゲーションボタン */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBack />}
            >
              戻る
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : <Check />}
                >
                  {submitting ? '送信中...' : '申請する'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                >
                  次へ
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PartnerApplyPage;
